import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Resolve the selected variant's featured media id from a ProductSelectEvent detail.
   * @param {any} detail
   * @returns {string|null}
   */
  #getSelectedMediaId(detail) {
    const resource = detail?.resource;
    const id =
      resource?.featured_media?.id ??
      resource?.featured_image?.id ??
      null;
    return id != null ? String(id) : null;
  }

  /**
   * Whether this gallery already contains a slide for the given media id.
   * @param {string} mediaId
   * @returns {boolean}
   */
  #hasSlideForMedia(mediaId) {
    const slides = this.slideshow?.refs?.slides;
    return Boolean(slides?.some((slide) => slide.getAttribute('slide-id') === mediaId));
  }

  /**
   * Scroll the slideshow to the slide matching the given media id.
   *
   * slideshow.select() bails with an early return when it believes the requested
   * slide is already the current one. After a gallery self-replace, the internal
   * `current` index can be desynced from the actual scroll position, so a plain
   * select() can no-op and the main image never moves. To guarantee a real scroll
   * we first set `current` to an ADJACENT index WITHOUT scrolling (no paint), then
   * select the true target. Adjacent (not slide 0) means no visible flash even if
   * a frame were to paint.
   *
   * @param {import('./slideshow').Slideshow | undefined} slideshow
   * @param {string} mediaId
   * @param {number} [attempt]
   */
  #forceSelectMediaById(slideshow, mediaId, attempt = 0) {
    if (!slideshow || !mediaId) return;

    const slides = slideshow.refs?.slides;
    if (!slides || slides.length === 0) {
      if (attempt < 10) {
        requestAnimationFrame(() => this.#forceSelectMediaById(slideshow, mediaId, attempt + 1));
      }
      return;
    }

    const targetIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === mediaId);
    if (targetIndex === -1) {
      if (attempt < 10) {
        requestAnimationFrame(() => this.#forceSelectMediaById(slideshow, mediaId, attempt + 1));
      }
      return;
    }

    // Desync `current` to an adjacent index (no scroll/paint) so select()'s
    // same-slide guard can't short-circuit the real selection.
    const decoyIndex = targetIndex === 0 ? Math.min(1, slides.length - 1) : targetIndex - 1;
    try {
      slideshow.current = decoyIndex;
    } catch (e) {
      // `current` setter should be writable; ignore if not.
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });

    // Quick-add modal case: the modal gallery is NOT a horizontal carousel. Its
    // slides are stacked vertically (flex-direction: column, scroll-snap: none)
    // and on desktop every slide stays visibility:visible, so toggling aria-hidden
    // or scrolling the horizontal scroller does nothing. The selected image is
    // shown by scrolling the vertical media container to that slide's offset.
    // On mobile, aria-hidden='true' slides are display:none, so we also set
    // aria-hidden so only the target shows.
    if (this.#isInModal()) {
      const targetSlide = slides[targetIndex];

      // Mobile: hide the non-target slides so the single visible one is correct.
      for (let i = 0; i < slides.length; i++) {
        slides[i].setAttribute('aria-hidden', i === targetIndex ? 'false' : 'true');
      }

      // Desktop: scroll the vertical media container to the target slide.
      // The container (.product-information__media) is replaced on variant change
      // and its images may not have height yet, so retry until it is actually
      // scrollable, then position it on the target slide.
      this.#scrollModalToSlide(targetSlide);
    }
  }

  /**
   * Scrolls the quick-add modal's vertical media container to a target slide,
   * retrying until the container has laid out (scrollHeight > clientHeight) so
   * the offset is computed against real geometry.
   * @param {HTMLElement} targetSlide
   * @param {number} [attempt]
   */
  #scrollModalToSlide(targetSlide, attempt = 0) {
    if (!targetSlide || !targetSlide.isConnected) return;

    const scrollContainer = targetSlide.closest('.product-information__media') || this.#closestScrollableFrom(targetSlide);
    if (!scrollContainer) return;

    const scrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
    if (!scrollable && attempt < 15) {
      requestAnimationFrame(() => this.#scrollModalToSlide(targetSlide, attempt + 1));
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const slideRect = targetSlide.getBoundingClientRect();
    const delta = slideRect.top - containerRect.top + scrollContainer.scrollTop;
    scrollContainer.scrollTo({ top: delta, behavior: 'auto' });
  }

  /**
   * Nearest vertically-scrollable ancestor starting from a given element.
   * @param {HTMLElement} from
   * @returns {HTMLElement | null}
   */
  #closestScrollableFrom(from) {
    let el = from.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
        return /** @type {HTMLElement} */ (el);
      }
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Whether this gallery is rendered inside the quick-add (or any) dialog.
   * @returns {boolean}
   */
  #isInModal() {
    return Boolean(this.closest('dialog'));
  }

  /**
   * Finds the nearest vertically-scrollable ancestor, as a fallback when the
   * known modal media container class isn't present.
   * @returns {HTMLElement | null}
   */
  #closestScrollable() {
    let el = this.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
        return /** @type {HTMLElement} */ (el);
      }
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Handles a product select event.
   *
   * If the selected media already exists in this gallery (simple variant change),
   * scroll the existing slideshow to it. Otherwise replace the gallery with the
   * freshly fetched HTML and select the target once it mounts.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectedMediaId = this.#getSelectedMediaId(event.detail);

    // Fast path: selected media already in this gallery — just scroll to it.
    if (selectedMediaId && this.#hasSlideForMedia(selectedMediaId)) {
      this.#forceSelectMediaById(this.slideshow, selectedMediaId);
      return;
    }

    event.promise
      .then(({ detail }) => {
        const mediaId = this.#getSelectedMediaId(detail) ?? selectedMediaId;
        const html = detail?.html;
        const newMediaGallery = html?.querySelector('media-gallery');

        if (newMediaGallery) {
          this.replaceWith(newMediaGallery);
          if (mediaId) {
            // Run the select/scroll on the NEW gallery instance so `this` (used by
            // the modal #isInModal()/#scrollModalToSlide logic) refers to the
            // connected element, not the old detached one.
            requestAnimationFrame(() => {
              newMediaGallery.#forceSelectMediaById(newMediaGallery.slideshow, mediaId);
            });
          }
          return;
        }

        if (mediaId) {
          this.#forceSelectMediaById(this.slideshow, mediaId);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}